import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import { useInfiniteHandler } from "hooks";
import { apiClient } from "utils";
import { useResponseHandler } from "utils/service/responseHandler";
import { SETTLEMENT_ORDER_COMMAND } from './enums';
import {
  ICancelSettlementOrderResponse,
  ICreateSettlementOrder,
  ICreateSettlementOrderLabels,
  ICreateSettlementOrderToNewest,
  ISettlementOrder,
  ISettlementOrderParam,
  ISettlementOrderResponse,
} from "./types";
import { queueRefetchInterval } from "constants/queueRefetchInterval";

export const useGetSettlementOrderListInfinite = (
  params: ISettlementOrderParam,
  startSettlementPeriodId?: string,
  endSettlementPeriodId?: string
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  return useInfiniteQuery<ISettlementOrder[], DefaultError>({
    queryKey: ["settlement-orders", params, startSettlementPeriodId, endSettlementPeriodId],

    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: "SettlementOrder/Orders",
        params: { 
          ...params, 
          page: pageParam,
          ...(startSettlementPeriodId && { startSettlementPeriodId }),
          ...(endSettlementPeriodId && { endSettlementPeriodId })
        },
      });

      const value = await responseHandler<ISettlementOrderResponse>(response, {
        groupName: `useGetSettlementOrderListInfinite - Page: ${pageParam}`,
      });

      return infiniteHandler(value);
    },

    initialPageParam: params.page,
    getNextPageParam: getNextPageParam,
    refetchInterval: queueRefetchInterval,
  });
};

export const useCreateSettlementOrder = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, ICreateSettlementOrder>({
    mutationFn: async (data) => {
      const response = await apiClient({
        method: "POST",
        url: `SettlementOrder`,
        body: data,
      });

      await responseHandler<void>(response, {
        groupName: "useCreateSettlementOrder",
      });
    },
  });
};

export const useCreateSettlementOrderToNewest = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, ICreateSettlementOrderToNewest>({
    mutationFn: async (data) => {
      const response = await apiClient({
        method: "POST",
        url: `SettlementOrder/Newest`,
        body: data,
      });

      await responseHandler<void>(response, {
        groupName: "useCreateSettlementOrderToNewest",
      });
    },
  });
};

export const useGetSettlementOrderLabels = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, ICreateSettlementOrderLabels>({
    mutationFn: async (params) => {
      const response = await apiClient({
        method: "POST",
        url: "SettlementOrder/Labels",
        body: params,
      });

      return await responseHandler<void>(response, {
        groupName: "useGetSettlementOrderLabels",
      });
    },
  });
};

export const useCancelSettlementOrder = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, ICancelSettlementOrderResponse>({
    mutationFn: async ({ labelId }) => {
      const response = await apiClient({
        method: "PUT",
        url: `SettlementOrder/Cancel`,
        body: { labelId },
      });

      await responseHandler<void>(response, {
        groupName: "useCancelSettlementOrder",
      });
    },
  });
};

export const useCancelAllSettlementOrders = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, {}>({
    mutationFn: async (data) => {
      const response = await apiClient({
        method: "PUT",
        url: "SettlementOrder/CancelAll",
        body: { command: SETTLEMENT_ORDER_COMMAND.cancelAll },
      });

      await responseHandler<void>(response, {
        groupName: "useCancelAllSettlementOrders",
      });
    },
  });
};

export interface IAcceptSettlementOrderParams {
  settlementOrderId: string;
}

export const useAcceptSettlementOrder = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, IAcceptSettlementOrderParams>({
    mutationFn: async ({ settlementOrderId }) => {
      const response = await apiClient({
        method: "PUT",
        url: "SettlementOrder/Accept",
        body: settlementOrderId,
      });

      await responseHandler<void>(response, {
        groupName: "useAcceptSettlementOrder",
      });
    },
  });
};

export interface IRejectSettlementOrderParams {
  settlementOrderId: string;
}

export const useRejectSettlementOrder = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, IRejectSettlementOrderParams>({
    mutationFn: async ({ settlementOrderId }) => {
      const response = await apiClient({
        method: "PUT",
        url: "SettlementOrder/Reject",
        body: settlementOrderId,
      });

      await responseHandler<void>(response, {
        groupName: "useRejectSettlementOrder",
      });
    },
  });
};